import type { ForeclosureInstigatorType } from "@/hera-api/foreclosure-instigator-api";
import type { ForeclosureCaseReview } from "@/hera-api/foreclosure-license-link-api";
import type { GovernmentForeclosureCaseListItem } from "@/hera-api/government-foreclosure-cases-api";
import type { ForeclosurePartyField } from "@/lib/foreclosure-case-party-utils";
import {
  FORECLOSURE_CASE_AGGREGATE_QUERY_KEY_PARTS,
  GOVERNMENT_FORECLOSURE_CASES_QUERY_KEY,
  OPERATOR_FORECLOSURE_CASES_QUERY_KEY,
} from "@/lib/foreclosure-case-query-keys";
import type { QueryClient } from "@tanstack/react-query";

function foreclosureCaseQueryPrefix(
  queryKey: readonly unknown[],
): string | null {
  const first = queryKey[0];
  if (
    first === OPERATOR_FORECLOSURE_CASES_QUERY_KEY ||
    first === GOVERNMENT_FORECLOSURE_CASES_QUERY_KEY
  ) {
    return first;
  }
  return null;
}

function isForeclosureCaseListQueryKey(queryKey: readonly unknown[]): boolean {
  return foreclosureCaseQueryPrefix(queryKey) != null;
}

function isAggregateSegment(segment: unknown): boolean {
  return (
    typeof segment === "string" &&
    FORECLOSURE_CASE_AGGREGATE_QUERY_KEY_PARTS.includes(segment)
  );
}

export function isForeclosureCaseListRowsQueryKey(
  queryKey: readonly unknown[],
): boolean {
  return (
    foreclosureCaseQueryPrefix(queryKey) != null &&
    !isAggregateSegment(queryKey[1])
  );
}

export function isForeclosureCaseChartQueryKey(
  queryKey: readonly unknown[],
): boolean {
  return (
    foreclosureCaseQueryPrefix(queryKey) != null &&
    isAggregateSegment(queryKey[1])
  );
}

/**
 * Drop cached Cases-list rows so the next visit fetches live-link config
 * instead of showing stale (often broken) case-number links.
 */
export function dropForeclosureCaseListQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) => isForeclosureCaseListQueryKey(query.queryKey),
  });
}

type ForeclosureCaseListCache = {
  cases?: GovernmentForeclosureCaseListItem[];
};

function caseRowMatches(
  row: GovernmentForeclosureCaseListItem,
  countyFips: string,
  caseNumber: string,
): boolean {
  return row.county_fips === countyFips && row.case_number === caseNumber;
}

/** Patch matching rows in every cached cases list. Does not refetch. */
export function patchCachedForeclosureCaseLists(
  queryClient: QueryClient,
  countyFips: string,
  caseNumber: string,
  patch: Partial<GovernmentForeclosureCaseListItem>,
): void {
  queryClient.setQueriesData(
    {
      predicate: (query) => isForeclosureCaseListRowsQueryKey(query.queryKey),
    },
    (old: ForeclosureCaseListCache | undefined) => {
      if (!old?.cases) {
        return old;
      }
      let changed = false;
      const cases = old.cases.map((row) => {
        if (!caseRowMatches(row, countyFips, caseNumber)) {
          return row;
        }
        changed = true;
        return { ...row, ...patch };
      });
      return changed ? { ...old, cases } : old;
    },
  );
}

export function reviewFieldsCachePatch(
  review: Pick<
    ForeclosureCaseReview,
    | "is_reviewed"
    | "review_status"
    | "review_comment"
    | "review_updated_at"
    | "review_updated_by_display"
  >,
): Partial<GovernmentForeclosureCaseListItem> {
  return {
    is_reviewed: review.is_reviewed,
    review_status: review.review_status,
    review_comment: review.review_comment,
    review_updated_at: review.review_updated_at ?? null,
    review_updated_by_display: review.review_updated_by_display ?? null,
  };
}

export function applyForeclosureCaseReviewToCachedLists(
  queryClient: QueryClient,
  review: ForeclosureCaseReview,
  extra?: Partial<GovernmentForeclosureCaseListItem>,
): void {
  patchCachedForeclosureCaseLists(
    queryClient,
    review.county_fips,
    review.case_number,
    { ...reviewFieldsCachePatch(review), ...extra },
  );
}

export function partyOverrideCachePatch(
  row: GovernmentForeclosureCaseListItem,
  field: ForeclosurePartyField,
  nextId: string | null,
  nextDisplay: string | null,
): Partial<GovernmentForeclosureCaseListItem> {
  if (field === "omt") {
    if (nextId == null) {
      return {
        omt_party_id: row.suggested_omt_party_id ?? null,
        omt_display: row.suggested_omt_display ?? null,
        is_omt_overridden: false,
      };
    }
    return {
      omt_party_id: nextId,
      omt_display: nextDisplay,
      is_omt_overridden: true,
    };
  }
  if (field === "ams") {
    if (nextId == null) {
      return {
        ams_party_id: row.suggested_ams_party_id ?? null,
        ams_display: row.suggested_ams_display ?? null,
        is_ams_overridden: false,
      };
    }
    return {
      ams_party_id: nextId,
      ams_display: nextDisplay,
      is_ams_overridden: true,
    };
  }
  if (nextId == null) {
    return {
      registrant_account_id: row.suggested_registrant_account_id ?? null,
      registrant_display: row.suggested_registrant_display ?? null,
      is_registrant_overridden: false,
    };
  }
  return {
    registrant_account_id: nextId,
    registrant_display: nextDisplay,
    is_registrant_overridden: true,
  };
}

/** Patch the cache after a start-date override is saved or cleared. */
export function startDateOverrideCachePatch(
  row: GovernmentForeclosureCaseListItem,
  review: Pick<
    ForeclosureCaseReview,
    "override_start_date" | "effective_start_date" | "is_start_date_overridden"
  >,
): Partial<GovernmentForeclosureCaseListItem> {
  return {
    override_start_date: review.override_start_date ?? null,
    effective_start_date: review.effective_start_date ?? row.start_date ?? null,
    is_start_date_overridden: review.is_start_date_overridden ?? false,
  };
}

/** Patch the cache after a case-number override is saved or cleared. */
export function caseNumberOverrideCachePatch(
  row: GovernmentForeclosureCaseListItem,
  review: Pick<
    ForeclosureCaseReview,
    | "override_case_number"
    | "effective_case_number"
    | "is_case_number_overridden"
  >,
): Partial<GovernmentForeclosureCaseListItem> {
  return {
    override_case_number: review.override_case_number ?? null,
    effective_case_number: review.effective_case_number ?? row.case_number,
    is_case_number_overridden: review.is_case_number_overridden ?? false,
  };
}

/**
 * Source stored by a case-level label. The list reads this lock, and the
 * instigator cell labels it "case override".
 */
const MANUAL_CASE_INSTIGATOR_SOURCE = "manual_override";

/** Fields a saved case override writes onto the matching list row. */
export function instigatorOverrideCachePatch(
  instigatorType: ForeclosureInstigatorType,
  confidence: number,
): Partial<GovernmentForeclosureCaseListItem> {
  return {
    instigator_type: instigatorType,
    instigator_confidence: confidence,
    instigator_source: MANUAL_CASE_INSTIGATOR_SOURCE,
  };
}

/**
 * Show a saved case override on every cached cases list immediately.
 * Charts, map pins, and the jurisdiction summary do not group by instigator
 * type, so callers should not refetch those aggregates for one switch.
 */
export function applyForeclosureCaseInstigatorToCachedLists(
  queryClient: QueryClient,
  countyFips: string,
  caseNumber: string,
  instigatorType: ForeclosureInstigatorType,
  confidence: number,
): void {
  patchCachedForeclosureCaseLists(
    queryClient,
    countyFips,
    caseNumber,
    instigatorOverrideCachePatch(instigatorType, confidence),
  );
}

/** Refresh chart aggregates without reloading the full cases list. */
export function invalidateForeclosureCaseChartQueries(
  queryClient: QueryClient,
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => isForeclosureCaseChartQueryKey(query.queryKey),
  });
}

/**
 * Reload case-list queries (license link, live-link config). Does not also
 * refetch charts, and callers should not follow with refetchQueries — that
 * double-fetched the full list.
 */
export function invalidateForeclosureCaseListRowQueries(
  queryClient: QueryClient,
): Promise<void> {
  return queryClient.invalidateQueries({
    predicate: (query) => isForeclosureCaseListRowsQueryKey(query.queryKey),
  });
}
